import type { useGetMarketChartArgs } from '@morfi/backend-sdk-v2/client';
import { getAvailableIntervals, TIMEFRAME_CONFIG } from '@/lib/chart/chart-factory';
import type { ChartInterval, YAxisProperty } from '@/lib/chart/types';
import type { HistoricalWinnerChartInterval } from './winner-chart-source';

export type WinnerChartRequest =
  | {
      readonly kind: 'live';
      readonly queryArgs: useGetMarketChartArgs;
    }
  | {
      readonly kind: 'historical';
      readonly interval: HistoricalWinnerChartInterval;
      readonly queryArgs: useGetMarketChartArgs;
    };

export const WINNER_WINDOW_SECONDS: Readonly<
  Record<Exclude<HistoricalWinnerChartInterval, 'all'>, number>
> = {
  '1h': TIMEFRAME_CONFIG['1h'].windowSeconds,
  '6h': TIMEFRAME_CONFIG['6h'].windowSeconds,
  '1d': TIMEFRAME_CONFIG['1d'].windowSeconds,
  '1w': TIMEFRAME_CONFIG['1w'].windowSeconds,
  '1mo': TIMEFRAME_CONFIG['1mo'].windowSeconds,
};
export function winnerChartRequest(
  marketPubkey: string,
  outcomeMint: string,
  outcomeIndex: number,
  interval: ChartInterval,
  anchorMinuteMs: number,
  tradingStartedAt: string,
  property: YAxisProperty,
  renderMode: 'line' | 'candle',
  targetBars: number,
): WinnerChartRequest {
  const queryArgs: useGetMarketChartArgs = {
    pubkey: marketPubkey,
    mints: [outcomeMint],
    outcomeIndex: String(outcomeIndex),
    property: property === 'price' ? 'price' : 'market_cap',
    chartType: renderMode === 'candle' ? 'ohlcv' : 'line',
    interval: 'all',
    ...(interval === 'live' ? {} : { targetBars }),
    ...(interval === 'live'
      ? {}
      : interval === 'all'
        ? { from: tradingStartedAt }
        : {
            from: new Date(anchorMinuteMs - WINNER_WINDOW_SECONDS[interval] * 1_000).toISOString(),
          }),
  };
  return interval === 'live'
    ? { kind: 'live', queryArgs }
    : { kind: 'historical', interval, queryArgs };
}

export function winnerAvailableIntervals(
  tradesFrom: string,
  anchorMinuteMs: number,
): ReadonlySet<ChartInterval> {
  const tradesFromMs = Date.parse(tradesFrom);
  if (!Number.isFinite(tradesFromMs) || anchorMinuteMs <= 0) return getAvailableIntervals(0);
  return getAvailableIntervals(Math.floor((anchorMinuteMs - tradesFromMs) / 1_000));
}
